'use client';
/**
 * ⑭ R14a — 마스트헤드 우측 «사용자별» 섬. 옛 Navigation 사용자 메뉴의 항목·동작을 그대로 옮겼다
 * (로그아웃은 이 메뉴가 사이트의 «유일한» 진입점이다 — 빠뜨리지 말 것).
 *
 * §0-5 서버 HTML 은 로그인/비로그인·봇/사람 동일 — AuthProvider.loading 동안(SSR·첫 수화)은 같은 크기(72×40) 빈 자리.
 * 로그인 링크는 옛 Navigation·HeaderAuth 와 같은 앵커(navLoginHref 'nav' + trackNavLoginClick).
 * ⛔ 「회원가입」 버튼은 두지 않는다(⑭ 가입 유도 0). 로그인 자체는 유지.
 * 알림 개수 조회·30초 폴링은 옛 Navigation 과 같은 방식(탭 활성 시만) — v2 에서는 이 섬 한 곳이 한다.
 */
import Link from 'next/link';
import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { Bell, PenSquare, Settings, MapPin, Lightbulb, LogOut, User, Megaphone } from 'lucide-react';
import { useAuth } from '@/components/AuthProvider';
import { createSupabaseBrowser } from '@/lib/supabase-browser';
import { navLoginHref, trackNavLoginClick } from '@/components/nav/nav-login';
import FontSizeControl from './FontSizeControl';

export default function NavUserMenu({ pathname }: { pathname: string }) {
  const { userId, loading, profile } = useAuth();
  const router = useRouter();
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    if (!userId) { setUnread(0); return; }
    const sb = createSupabaseBrowser();
    const load = async () => {
      if (document.visibilityState !== 'visible') return;
      try {
        const { count } = await sb.from('notifications').select('id', { count: 'exact', head: true }).eq('user_id', userId).eq('is_read', false);
        setUnread(count ?? 0);
      } catch { /* 표시만 — 실패는 삼킨다 */ }
    };
    // R14perf — 첫 조회는 유휴 시간으로 미룬다(수화·첫 그리기와 경쟁하지 않게). 폴링·가시성 재조회는 그대로.
    const ric = typeof window.requestIdleCallback === 'function'; // Safari 는 없다 → setTimeout
    const idle = ric ? window.requestIdleCallback(() => { load(); }, { timeout: 3000 }) : window.setTimeout(load, 1);
    const id = setInterval(load, 30000);
    document.addEventListener('visibilitychange', load);
    return () => {
      if (ric) window.cancelIdleCallback(idle); else clearTimeout(idle);
      clearInterval(id);
      document.removeEventListener('visibilitychange', load);
    };
  }, [userId]);

  if (loading) {
    return <span className="kd-user"><span className="kd-user__ph" aria-hidden="true" /></span>;
  }

  if (!userId) {
    return (
      <span className="kd-user">
        <Link href={navLoginHref(pathname, 'nav')} onClick={trackNavLoginClick} className="kd-user__login" prefetch={false}>
          로그인
        </Link>
      </span>
    );
  }

  const logout = async () => {
    await createSupabaseBrowser().auth.signOut();
    router.push(`/login?redirect=${encodeURIComponent(pathname)}`);
  };
  const initial = (profile?.nickname ?? 'U').slice(0, 1).toUpperCase();
  const items = [
    { href: `/profile/${userId}`, label: '내 프로필', Icon: User },
    { href: '/write', label: '글쓰기', Icon: PenSquare },
    { href: '/notifications', label: unread > 0 ? `알림 (${unread})` : '알림', Icon: Bell },
    { href: '/notifications/settings', label: '알림 설정', Icon: Settings },
    { href: '/settings/region', label: '지역 설정', Icon: MapPin },
    { href: '/settings/interests', label: '관심사 설정', Icon: Lightbulb },
    // ads-ux — 광고주가 결제한 광고(상태·소재 수정·연장·환불)로 가는 고정 입구. 로그인 후에만 보이는 메뉴라 서버 HTML 은 그대로.
    { href: '/ads/my', label: '내 광고', Icon: Megaphone },
  ];

  return (
    <span className="kd-user">
      <details className="kd-user__menu">
        <summary className="kd-user__btn" aria-label={unread > 0 ? `사용자 메뉴 · 읽지 않은 알림 ${unread}건` : '사용자 메뉴'}>
          {profile?.avatarUrl ? <img src={profile.avatarUrl} alt="" width={32} height={32} /> : <span className="kd-user__initial" aria-hidden="true">{initial}</span>}
          {unread > 0 ? <span className="kd-user__badge" aria-hidden="true">{unread > 9 ? '9+' : unread}</span> : null}
        </summary>
        <div className="kd-user__panel">
          <p className="kd-user__name">{profile?.nickname ?? '내 계정'}</p>
          <ul className="kd-user__list">
            {items.map(({ href, label, Icon }) => (
              <li key={href}>
                <Link href={href} className="kd-user__item" prefetch={false}>
                  <Icon size={16} aria-hidden="true" />
                  {label}
                </Link>
              </li>
            ))}
          </ul>
          <div className="kd-user__font">
            <FontSizeControl compact />
          </div>
          <button type="button" className="kd-user__item kd-user__logout" onClick={logout}>
            <LogOut size={16} aria-hidden="true" /> 로그아웃
          </button>
        </div>
      </details>
    </span>
  );
}
